import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import LinkConstants from "../../../../constants/LinkConstants";
import BriaBadge from "../../../common/BriaBadge/BriaBadge";
import styles from "./OverviewIntegrations.module.scss";

import AWSIcon from "../../../../assets/Integrations/icons/aws.svg";
import AzureIcon from "../../../../assets/Integrations/icons/azure.svg";
import ComfyUIIcon from "../../../../assets/Integrations/icons/comfyui.png";
import CopilotIcon from "../../../../assets/Integrations/icons/copilot.svg";
import FalIcon from "../../../../assets/Integrations/icons/fal.svg";
import FigmaIcon from "../../../../assets/Integrations/icons/figma.png";
import HuggingFaceIcon from "../../../../assets/Integrations/icons/huggingface.svg";
import InvokeIcon from "../../../../assets/Integrations/icons/invoke.svg";
import PhotoshopIcon from "../../../../assets/Integrations/icons/photoshop.png";
import PowerPointIcon from "../../../../assets/Integrations/icons/powerpoint.svg";

export type IntegrationOption =
	| "HUGGING_FACE"
	| "FAL"
	| "COMFY_UI"
	| "INVOKE"
	| "FIGMA"
	| "PHOTOSHOP"
	| "AWS"
	| "AZURE"
	| "COPILOT"
	| "POWERPOINT";

type IntegrationLinkCardProps = {
	icon: string;
	label: string;
	link?: LinkConstants;
	badgeLabel?: string;
};

type OverviewIntegrationsProps = {
	cards: IntegrationOption[];
	className?: string;
};

const iconMap: Record<IntegrationOption, string> = {
	HUGGING_FACE: HuggingFaceIcon,
	FAL: FalIcon,
	COMFY_UI: ComfyUIIcon,
	INVOKE: InvokeIcon,
	FIGMA: FigmaIcon,
	PHOTOSHOP: PhotoshopIcon,
	AWS: AWSIcon,
	AZURE: AzureIcon,
	COPILOT: CopilotIcon,
	POWERPOINT: PowerPointIcon,
};

export const OverviewIntegrations = ({ cards, className }: OverviewIntegrationsProps) => {
	const { t } = useTranslation("translation", { keyPrefix: "overviewIntegrations" });
	const defaultIntegrations: { [key in IntegrationOption]: Omit<IntegrationLinkCardProps, "icon"> } = t("options", {
		returnObjects: true,
	});

	return (
		<Box className={`${styles.integrations} ${className}`}>
			<Typography className={styles.title}>{t("title")}</Typography>
			<Box className={styles.cards}>
				{cards.map((integration, index) => (
					<IntegrationLinkCard
						key={index}
						icon={iconMap[integration]}
						{...defaultIntegrations[integration]}
						link={LinkConstants.INTEGRATIONS[integration]}
					/>
				))}
			</Box>
		</Box>
	);
};

const IntegrationLinkCard = ({ icon, label, link, badgeLabel }: IntegrationLinkCardProps) => {
	const cardContent = (
		<>
			<img className={styles.icon} src={icon} alt={label} />
			<Typography className={styles.label}>{label}</Typography>
			{badgeLabel && <BriaBadge className={styles.badge} label={badgeLabel} />}
		</>
	);

	if (link) {
		return (
			<NavLink className={styles.clickableCard} to={link} target="_blank">
				{cardContent}
			</NavLink>
		);
	}

	return <Box className={styles.card}>{cardContent}</Box>;
};
